import tw from 'twin.macro'
import { useEffect, useMemo, useRef, useState } from 'react'
import { graphql } from 'gatsby'
import { useIsClient, useMediaQuery, useResizeObserver } from 'usehooks-ts'
import RichText from '@/components/ui/rich-text'
import { getTimeZone, getCurrentTimeZoneIdentifier, isDaylightSavingTime } from '@/utils/dates'
import useStickyTracker from '@/utils/useStickyHelper'
import colors from '@/constants/colors'

import Event from './event'

export const query = graphql`
  fragment EventSchedule on ContentfulSectionEventSchedule {
    __typename
    id
    title
    description {
      raw
    }
    date
    events {
      id
      type
      title
      startTime
      isCustomTime
      customTime
      category
      speakers
    }
  }
`

export type EventScheduleProps = Queries.EventScheduleFragment & {
  backgroundColor?: string
}

export default function EventSchedule({
  title,
  description,
  date: eventDate,
  events,
  backgroundColor = colors.white,
}: EventScheduleProps) {
  const isClient = useIsClient()
  const isMobile = useMediaQuery('(max-width: 1024px)')
  const { stickyTracker, top, isStuck } = useStickyTracker({ ignoreStickyCta: isMobile })

  const titleRef = useRef<HTMLDivElement>(null)
  const [titleHeight, setTitleHeight] = useState(0)
  useEffect(() => {
    if (titleRef.current) {
      setTitleHeight(titleRef.current.getBoundingClientRect().height)
    }
  }, [titleRef.current])
  useResizeObserver({
    ref: titleRef,
    onResize: () => {
      setTitleHeight(titleRef.current?.getBoundingClientRect().height ?? 0)
    },
  })

  const timezoneOptions = useMemo(() => {
    // Get current timezone
    const date = new Date()
    const currentTimezone = {
      name: getTimeZone(date, 'short'),
      tz: getCurrentTimeZoneIdentifier(),
    }
    // Use timezone identifiers since it's more reliable across browsers
    const timeZones = {
      [currentTimezone.name]: currentTimezone.tz,
      EDT: 'America/New_York',
      EST: 'America/New_York',
      PST: 'America/Los_Angeles',
      PDT: 'America/Los_Angeles',
      MST: 'America/Denver',
      MDT: 'America/Denver',
      CST: 'America/Chicago',
      CDT: 'America/Chicago',
      CET: 'Europe/Berlin',
      CEST: 'Europe/Berlin',
    }
    const timezoneSet = new Set([
      currentTimezone.name,
      ...(isDaylightSavingTime(new Date(eventDate))
        ? ['PDT', 'MDT', 'CST', 'EDT', 'CEST']
        : ['PST', 'MST', 'CST', 'EST', 'CET']),
    ])
    return Array.from(timezoneSet)
      .slice(0, 5)
      .map((name) => ({ name, offset: timeZones[name] }))
  }, [])

  const [timezone, setTimeZone] = useState<{ name: string; offset: string }>(timezoneOptions[0])

  return (
    <div tw="relative max-w-8xl mx-auto lg:px-9">
      <div tw="flex flex-col lg:flex-row justify-center lg:items-baseline lg:gap-3 w-full">
        <div
          css={[tw`flex-shrink contents pb-3 [&>*]:(px-5 lg:px-0)`, tw`lg:(w-[416px] sticky block pt-0)`]}
          style={{ top }}
        >
          {stickyTracker}
          <div
            ref={titleRef}
            tw="text-4xl font-bold pt-4 pb-3 sticky z-50 lg:static shadow-lg"
            css={[
              backgroundColor === colors.white && tw`bg-white shadow-white`,
              backgroundColor === colors.lightGray && tw`bg-gray-100 shadow-gray-100`,
              backgroundColor === colors.primaryBlue && tw`bg-primaryBlue shadow-primaryBlue`,
              backgroundColor === colors.indigoBlue && tw`bg-indigoBlue shadow-indigoBlue`,
            ]}
            style={{ top }}
          >
            {title}
          </div>
          <div
            css={[
              tw`text-lg pt-4 z-40`,
              backgroundColor === colors.white && tw`bg-white`,
              backgroundColor === colors.lightGray && tw`bg-gray-100`,
              backgroundColor === colors.primaryBlue && tw`bg-primaryBlue`,
              backgroundColor === colors.indigoBlue && tw`bg-indigoBlue`,
            ]}
          >
            <RichText body={description as any} />
          </div>
          {isClient && (
            <div
              css={[
                tw`flex flex-wrap gap-6 pt-3 pb-3 sticky z-50 lg:(static gap-3)`,
                isStuck && tw`shadow-xl lg:shadow-none`,
                backgroundColor === colors.white && tw`bg-white`,
                backgroundColor === colors.lightGray && tw`bg-gray-100`,
                backgroundColor === colors.primaryBlue && tw`bg-primaryBlue`,
                backgroundColor === colors.indigoBlue && tw`bg-indigoBlue`,
              ]}
              style={{ top: top + titleHeight }}
            >
              {timezoneOptions.map((tz) => (
                <button
                  key={tz.name}
                  type="button"
                  css={[
                    tw`lg:(rounded-full border-2 text-base leading-[30px] px-4 py-2)`,
                    timezone.name === tz.name
                      ? tw`border-primaryBlue text-primaryBlue`
                      : tw`border-gray-400 text-gray-400`,
                  ]}
                  onClick={() => setTimeZone(tz)}
                >
                  {tz.name}
                  <span tw="inline-block lg:hidden svg:fill-none w-[8px] ml-[4px]">
                    <svg tw="w-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 12">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m1.927 1 5 5-5 5"
                      />
                    </svg>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div tw="flex flex-col px-5 gap-3 flex-1">
          {events?.map((event) => {
            switch (event.type) {
              case 'section':
                return (
                  <h3 key={event.id} tw="text-2xl font-bold mt-3 mb-0">
                    {event.title}
                  </h3>
                )
              case 'event':
                return (
                  <Event
                    key={event.id}
                    {...event}
                    date={eventDate}
                    timeZone={timezone.offset}
                    backgroundColor={backgroundColor}
                  />
                )
              default:
                return null
            }
          })}
        </div>
      </div>
    </div>
  )
}
